import React from "react";
import { useDelete, usePatch, usePost } from "./useCall";
import useQuery, { useMutate } from "./useQuery";
import { uniq } from "diagnostics-lib/array";
const useAllTypesCategories = () => {
  const { body: data } = useQuery("/categories", { keepFresh: true });

  const sortedData = React.useMemo(
    () =>
      Object.entries(data).reduce<typeof data>(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.sort(),
        }),
        {} as typeof data,
      ),
    [data],
  );

  return sortedData;
};

export type CategoryType = keyof ReturnType<typeof useAllTypesCategories>;
export type Category = ReturnType<typeof useAllTypesCategories>[CategoryType][number];

export const useCategories = (type: CategoryType) => useAllTypesCategories()[type];

export const useUpdateCategory = (type: CategoryType) => {
  const mutate = useMutate("/categories");
  const patch = usePatch("/categories/:type(categoryType)");

  return React.useCallback(
    async (oldName: string, newName: string) => {
      const value = await patch({
        routeParams: { type },
        body: { currentId: oldName, newId: newName },
      });
      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: uniq([...body[type].filter(category => category !== oldName), newName]).sort(),
        },
      }));
      return value;
    },
    [mutate, patch, type],
  );
};

export const useDeleteCategory = (type: CategoryType) => {
  const mutate = useMutate("/categories");
  const del = useDelete("/categories/:type(categoryType)");

  return React.useCallback(
    async (name: string) => {
      const value = await del({ routeParams: { type }, body: { id: name } });
      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: body[type].filter(category => category !== name),
        },
      }));
      return value;
    },

    [del, mutate, type],
  );
};

export const useCreateCategory = (type: CategoryType) => {
  const mutate = useMutate("/categories");
  const post = usePost("/categories/:type(categoryType)");

  return React.useCallback(
    async (name: string) => {
      const value = await post({ routeParams: { type }, body: { id: name } });
      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: [...body[type], name].sort(),
        },
      }));

      return value;
    },
    [mutate, post, type],
  );
};
